import { useEffect } from 'react';
import { VideoBackground } from '@rocco/ui/banner/components/VideoBackground';
import type { LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { Button } from '@rocco/components/button';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';
import { ReactComponent as PlayIcon } from '@rocco/icons/svg/play.svg';
import { ToggleGroup, ToggleGroupItem } from '@rocco/components/toggle-group';
import { VideoPlayer } from '@rocco/components/video-player/video-player';
import { useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { ClientOnly } from 'remix-utils';
import { HomeCarouselSection } from '@rocco/ui/layout/components/HomeCarouselSection';
import type { HomePageIntroProps } from '~/ui-rocco/pages/home-page/HomePageIntro';
import { HomePageIntro } from '~/ui-rocco/pages/home-page/HomePageIntro';
import { getHomeIntro } from '~/modules-rocco/home/api/get-home-intro.server';
import { Image } from '@rocco/components/image';

import {
    getCanonicalHreflang,
    createMetaTitle,
    createOGTags,
    createMetaDescription,
    mergeMeta,
} from '~/modules/seo';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { HomePageTestimonials } from '~/ui-rocco/pages/home-page/HomePageTestimonials';
import { getHomeTestimonial } from '~/modules-rocco/home/api/get-home-testimonial.server';
import { PageBottomSection } from '~/ui-rocco/pages/shared/PageBottomSection';
import { collectFallibleResources } from '~/modules-rocco/root/api/collect-fallible-resources.server';
import type { TrackingEvent } from '@archipro-website/tracker';
import { useTracker } from '@archipro-website/tracker';
import { useUpdateEffect } from 'ahooks';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { useLocation } from '@remix-run/react';
export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const title = createMetaTitle(props, [
            'Essential Guides For Homeowners',
        ]);
        const description = createMetaDescription([
            'Building or renovating a home is an exciting yet complex process. ArchiPro\'s "What I Wish I\'d known" series brings together expert advice, real homeowner experiences, and practical tools to guide you through each stage of your journey.',
        ]);

        return [
            {
                title,
            },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
        ];
    }
);
/**
 * Defines the structure for the banner video data.
 */
interface BannerVideoData {
    /** The main title displayed on the banner. */
    title: string;
    /** A short description accompanying the title. */
    description: string;
    /** URL for the MP4 video source. */
    videoMp4: string;
    /** URL for the WebM video source (optional). */
    videoWebm?: string;
    /** URL for the poster image displayed before video loads or if video fails. */
    imageUrl: string;
    /** Text for the call-to-action button. */
    ctaText: string;
    /** URL the call-to-action button links to. */
    ctaUrl: string;
    /** ID for the target element. */
    targetID?: string;
}

/**
 * Base interface for sections, defining common properties like background theme.
 */
interface Section {
    /** Background theme for the section. */
    bgTheme: 'gray' | 'white' | 'light-gray';
}

interface BillboardSection {
    /** The main title of the Image Billboard. */
    title: string;
    /** A subtitle providing more context. */
    subtitle: string;
    /** An array of guide cards to display in the billboard. */
    guideCards: GuideCardData[];
}

/**
 * Defines the structure for individual descriptive sections within an Image Billboard.
 */
interface ImageBillboardSectionItem {
    /** The descriptive text for the section. */
    description: string;
}

/**
 * Defines the structure for an Image Billboard component.
 */
interface ImageBillboardData extends Section {
    /** The main title of the Image Billboard. */
    title: string;
    /** A subtitle providing more context. */
    subtitle: string;
    /** URL for the main image displayed in the billboard. */
    poster: string;
    /** Text for the call-to-action button. */
    ctaText: string;
    /** URL the call-to-action button links to. */
    ctaUrl: string;
    /** An array of descriptive sections (up to 3 displayed). */
    sections: ImageBillboardSectionItem[];
}

/**
 * Defines the structure for a Video Billboard component.
 */
interface VideoBillboardData extends Section {
    /** URL of the video to be played. */
    videoUrl: string;
    /** URL for the poster image displayed before video playback. */
    posterDesktop: string;
    posterMobile: string;
    /** The main title of the Image Billboard. */
    title: string;
    /** A subtitle providing more context. */
    subtitle: string;
    /** Text for the call-to-action button. */
    ctaText: string;
    /** URL the call-to-action button links to. */
    ctaUrl: string;
    /** Whether the call-to-action button is disabled. */
    disabled?: boolean;
    /** The file name of the guide. */
    fileName: string;
}

/**
 * Defines the structure for a single item within a carousel.
 */
interface CarouselItemData {
    /** URL of the image for the carousel item. */
    imageUrl: string;
    /** Title text displayed below the image. */
    title: string;
    /** Description text displayed below the image. */
    description: string;
}

/**
 * Defines the structure for a Carousel section.
 */
interface CarouselData extends Section {
    /** The title of the carousel section. */
    title: string;
    /** A description for the carousel section. */
    description: string;
    /** An array of items to display in the carousel. */
    items: CarouselItemData[];
}

/**
 * Combines the potential data modules (image and video) for a single guide's detail view.
 */
interface GuideDetailData {
    /** Data for the image billboard module, if present. */
    imageModule?: ImageBillboardData;
    /** Data for the video billboard module, if present. */
    videoModule?: VideoBillboardData;
}

/**
 * Represents a complete guide, including its card representation and detailed content modules.
 */
interface Guide {
    /** Unique identifier for the guide. */
    id: string;
    /** The full title of the guide. */
    title: string;
    /** A short description of the guide's content. */
    description: string;
    /** URL for the image used in the tab selector card. */
    imageUrl: string;
    /** Detailed content modules (image/video billboards) for the guide. */
    guideDetail: GuideDetailData;
    /** CTA button data for the guide. */
    ctaText: string;
    ctaUrl: string;
    /** The file name of the guide. */
    fileName: string;
}

/**
 * Data specifically structured for the GuideCard component used within the tab selector.
 */
interface GuideCardData {
    /** Unique identifier for the guide. */
    id: string;
    /** The full title of the guide. */
    title: string;
    /** A short description of the guide's content. */
    description: string;
    /** URL for the image displayed on the card. */
    imageUrl: string;
    /** CTA button data for the guide card. */
    ctaText: string;
    ctaUrl: string;
    /** The file name of the guide. */
    fileName: string;
}

interface ButtonCardData {
    title: string;
    description: string;
    video?: {
        videoCover: string;
        videoMp4: string;
        videoWebM: string;
        autoplay: boolean;
    };
    ctaText: string;
    ctaUrl: string;
    color: 'brick' | 'gray';
    type: 'default' | 'email';
}

interface CardTripletData extends Section {
    title: string;
    buttonCard: ButtonCardData[];
}

const themeColors = {
    gray: 'bg-[#dcdcdc]',
    white: 'bg-white',
    'light-gray': 'bg-[#f5f5f5]',
};

/**
 * Loads the necessary data for the Essential Guides For Homeowners page.
 * This includes banner information, guide details, and carousel content.
 * @param args - Remix loader arguments.
 * @returns A JSON response containing page data.
 */
const loader = async ({ context }: LoaderFunctionArgs) => {
    const { user, appDisplayMode } = context;
    const assetsBaseUrl = context.config.assetsBaseUrl;
    const isLoggedIn = !!user && user.__typename === 'Me';
    const isMobile = appDisplayMode === 'mobile';
    // Data is currently hardcoded. Replace with actual data fetching logic (e.g., GraphQL query) when available.
    const bannerVideoModuleData: BannerVideoData = {
        title: `The essential guides for building your dream home`,
        description: `Building or renovating a home is an exciting yet complex and time consuming process. ArchiPro’s "What I Wish I’d Known" series brings together expert advice, real homeowner experiences, and practical tools to guide you through each stage of your journey.`,
        videoMp4:
            'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1%2F67e1ec651c0fa7f25139bd16_WIWIK-Hero2-transcode.mp4',
        // videoWebm: '', // Optional: Add WebM source if available
        imageUrl:
            'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1%2F67e1ec651c0fa7f25139bd16_WIWIK-Hero2-poster-00001.jpg',
        ctaText: 'Free Download',
        ctaUrl: '/app/guides',
        targetID: 'main-content',
    };
    const noVideoBanner =
        !bannerVideoModuleData ||
        (!bannerVideoModuleData.videoMp4 && !bannerVideoModuleData.imageUrl);

    const guides: Guide[] = [
        {
            id: 'guide1',
            title: `Guide one: What I wish I'd known about setting up for success`,
            description:
                'Master the early planning stages, from setting priorities and understanding key pre-build steps to managing timelines and budgets.',
            imageUrl:
                'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e1bfb570b30ce93386c1a5_guide1.png',
            ctaText: 'Download Guide One',
            ctaUrl: '/app/guides',
            fileName: 'Guide1-NZ.pdf',
            guideDetail: {
                videoModule: {
                    title: 'Setting up for success',
                    subtitle: `<p>In this episode, homeowner Aysha shares her experience navigating the early planning stages, followed by seasoned builder Marshall Keen offering expert insights on setting priorities, understanding key pre-build stages, and creating a budget that works. Watch now to set your project on the right path and download your free guide for essential pre-build tips.</p>`,
                    posterDesktop:
                        'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e1f9576b5f1e5542cdef93_guidev1.png',
                    posterMobile: `${assetsBaseUrl}/assets/website/homepage/rocco/guide1-mobile-video-poster-new.jpg`,
                    ctaText: 'Download Guide One',
                    ctaUrl: '/app/guides',
                    bgTheme: 'gray',
                    videoUrl: 'https://youtu.be/k7qUwspQIDU',
                    fileName: 'Guide1-NZ.pdf',
                },
            },
        },
        {
            id: 'guide2',
            title: `Guide two: What I wish I'd known about creating a brief`,
            description:
                'Understand the roles of architects, designers and builders, and how to build the right team for your project.',
            imageUrl:
                'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e1c22f16d8814ef8223e34_guide2.png',
            ctaText: 'Download Guide Two',
            ctaUrl: '/app/guides',
            fileName: 'Guide2-NZ.pdf',
            guideDetail: {
                videoModule: {
                    title: 'Creating a brief',
                    subtitle: `<p>In this episode, homeowner Chloe shares her lessons learned from the design process, followed by renowned interior designer Jillian Dinkel offering expert insights on gathering inspiration, defining your style, and communicating effectively with professionals. Watch now to master the art of creating a design brief and download your free guide for expert advice.</p>`,
                    ctaText: 'Download Guide Two',
                    ctaUrl: '/app/guides',
                    posterDesktop:
                        'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e1f97156f101778663e9a5_guide%202.png', // Reusing poster from guide 2
                    posterMobile: `${assetsBaseUrl}/assets/website/homepage/rocco/guide2-mobile-video-poster-new.jpg`,
                    bgTheme: 'white',
                    videoUrl: 'https://youtu.be/FZ2zyz3QIq0',
                    fileName: 'Guide2-NZ.pdf',
                },
            },
        },
        {
            id: 'guide3',
            title: `Guide three: What I wish I'd known about working with professionals`,
            description:
                'It explains what different professionals do, how to find the right ones to work with and the questions you need to ask them to ensure the success of your project.',
            imageUrl:
                'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e1c52426a7a864a2937c96_guide3.png',
            ctaText: 'Download Guide Three',
            ctaUrl: '/app/guides',
            fileName: 'Guide3-NZ.pdf',
            guideDetail: {
                videoModule: {
                    title: 'Working with professionals',
                    subtitle: `<p>In this upcoming episode, award-winning architect Georgina Wilson shares expert advice on finding the right architects, designers, and builders, understanding their roles, managing expectations, and avoiding common challenges. Stay tuned, and download your free guide to start building the right team for your project.</p>`,
                    ctaText: 'Download Guide Three',
                    ctaUrl: '/app/guides',
                    disabled: false,
                    posterDesktop: `${assetsBaseUrl}/assets/website/homepage/rocco/guide-3-poster-placeholder.png`,
                    posterMobile: `${assetsBaseUrl}/assets/website/homepage/rocco/guide-3-poster-placeholder-mobile.png`,
                    bgTheme: 'gray',
                    videoUrl: '',
                    fileName: 'Guide3-NZ.pdf',
                },
            },
        },
    ];

    // Map full guides data to the specific format needed for GuideCard components
    const guideCards: GuideCardData[] = guides.map((guide) => ({
        id: guide.id,
        title: guide.title,
        description: guide.description,
        imageUrl: guide.imageUrl,
        ctaText: guide.ctaText,
        ctaUrl: guide.ctaUrl,
        fileName: guide.fileName,
    }));

    // Data for the billboard section
    const billboardSection: BillboardSection = {
        title: 'Plan with confidence. Build like a Pro.',
        subtitle: `<p>After a decade of conversations with homeowners, we’ve uncovered the often-overlooked aspects that can make or break a project.<br>
These guides will equip you with the knowledge, tools and expert tips to confidently navigate your build or renovation.</p>`,
        guideCards,
    };

    // Data for the "Campaign Performance" carousel
    const campaignPerformanceCarousel: CarouselData = {
        title: 'Meet the Pro experts behind the series',
        description: `Bringing industry-leading expertise and in-depth knowledge, our Pros of architects, interior designers and builders are here to guide you through every stage of your home-building journey.`,
        bgTheme: 'light-gray',
        items: [
            {
                imageUrl:
                    'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e212fc25c6925c33e2b5c9_3.png',
                title: 'Jillian Dinkel',
                description: `Jillian Dinkel is an award-winning interior designer and creative storyteller, renowned for transforming heritage and period homes into timeless sanctuaries.`,
            },
            {
                imageUrl:
                    'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e47de9644e1b7b52a9ef95_georgina%20wilson.png',
                title: 'Georgina Wilson',
                description: `Georgina Wilson is the founder and principal Architect of Georgina Wilson Associates, with over 20 years of experience designing award-winning homes and commercial spaces.`,
            },
            {
                imageUrl:
                    'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e212efac5eb951fb6d9a5f_2.png',
                title: 'Marshall Keen',
                description: `With over 20 years in the industry, Marshall Keen is a skilled builder and carpenter with an eye for design and detail. He founded Built by MK, focusing on bespoke commercial and residential projects.`,
            },
            {
                imageUrl:
                    'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e212dafa20a8616493f070_Peter%20Maddison.png',
                title: 'Peter Maddison',
                description: `Peter Maddison, former host of Grand Designs Australia, is an award-winning architect and Director of Maddison Architects and an A+ member of the Australian Institute of Architects.`,
            },
        ],
    };
    const [intro, testimonialSection] = await collectFallibleResources(
        [getHomeIntro(context), getHomeTestimonial(context)],
        context.logger
    );
    let videoMp4 = '';
    let videoWebM = '';
    let videoCover = '';
    if (intro) {
        videoMp4 = intro.video?.videoMp4 ?? '';
        videoWebM = intro.video?.videoWebM ?? '';
        videoCover =
            intro.video?.videoCover ??
            'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e080c2551240c0caa242d4_harness%20image.png';
    }

    const introData: HomePageIntroProps = {
        title: 'Everything begins with ArchiPro',
        description:
            'Planning a build or renovation can feel overwhelming, ArchiPro simplifies the process by connecting you with top architects, designers, and products to bring your project to life.',
        listItems: [],
        CTAButton: [
            {
                text: 'Join ArchiPro',
                link: '/app',
                color: 'primary',
            },
            {
                text: 'Learn More',
                link: '/',
                color: 'transparent',
            },
        ],
        video: {
            videoCover,

            videoMp4,

            videoWebM,
            autoplay: true,
        },
        openLogin: (url: string) => {
            window.location.href = url;
        },
    };

    const resourcesToGuideYou: CardTripletData = {
        title: 'Resources to guide you',
        buttonCard: [
            {
                title: 'Start your project',
                description:
                    'Start you project with a free account to unlock features designed to help you simplify your building project.',
                ctaText: 'Join ArchiPro',
                ctaUrl: '/app',
                color: 'brick',
                type: 'default',
            },
            {
                title: 'Stay informed',
                description:
                    'Stay up to date about the newest projects, products, and technologies shaping the future of the architecture and design environment.',
                ctaText: 'Submit',
                ctaUrl: '',
                color: 'gray',
                type: 'email',
                video: {
                    videoCover:
                        'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e080c2551240c0caa242d4_harness%20image.png',
                    videoMp4:
                        'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e080c2551240c0caa242ff_newsletter%20video-transcode.mp4',
                    videoWebM:
                        'https://cdn.prod.website-files.com/67e080c2551240c0caa241e1/67e080c2551240c0caa242ff_newsletter%20video-transcode.webm',
                    autoplay: true,
                },
            },
            {
                title: 'Explore Archizeen',
                description:
                    'Be informed and make the best decisions for your project. We bring you insights and interviews from those at the forefront of architecture and construction.',
                ctaText: 'Explore Now',
                ctaUrl: '/articles',
                color: 'gray',
                type: 'default',
            },
        ],
        bgTheme: 'gray',
    };

    return json({
        pageTheme: !noVideoBanner ? 'transparent' : 'default',
        bannerVideoModuleData,
        noVideoBanner,
        billboardSection,
        guides,
        campaignPerformanceCarousel,
        testimonialSection,
        introData,
        resourcesToGuideYou,
        isLoggedIn,
        isMobile,
        appDisplayMode,
    });
};

interface BannerVideoModuleProps {
    /** Data object containing details for the banner video. */
    bannerVideoModuleData: BannerVideoData;
    /** Flag indicating if the banner video data is missing or incomplete. */
    noVideoBanner: boolean;
    isMobile: boolean;
    openLogin: (url: string) => void;
}

/**
 * Displays the hero banner section with a background video.
 * @param props - Component props.
 * @param props.bannerVideoModuleData - Data for the banner video.
 * @param props.noVideoBanner - Flag indicating if the banner should be hidden.
 * @returns The BannerVideoModule component or null if no video data.
 */
const BannerVideoModule = ({
    bannerVideoModuleData,
    noVideoBanner,
    isMobile,
    openLogin,
}: BannerVideoModuleProps) => {
    const { isLoggedIn } = useLoaderData<typeof loader>();
    const tracker = useTracker();

    const log = (eventName: TrackingEvent) => {
        tracker.log(eventName, {
            data: {
                extraData: {
                    sectionName: 'LandingPageHero',
                },
            },
            url: new URL(window.location.href),
        });
    };

    if (noVideoBanner) {
        return null;
    }

    const {
        title,
        description,
        videoMp4,
        videoWebm,
        imageUrl,
        ctaText,
        ctaUrl,
        targetID,
    } = bannerVideoModuleData;

    return (
        <div className="relative flex h-188 w-full items-center justify-center md:h-screen md:justify-start">
            <VideoBackground
                videoMp4={videoMp4}
                videoWebM={videoWebm || ''}
                videoCover={imageUrl}
                autoplay={true}
                loop={true}
                overlay={true}
                positioning={isMobile ? 'relative' : 'absolute'}
            />
            <div className="absolute top-45 z-10 flex w-full flex-col gap-13 pl-4 text-white md:static md:ml-12 md:w-240 md:pl-0">
                <h2 className="font-regular text-12 leading-[1.0] tracking-[-2px] md:text-20">
                    {title}
                </h2>
                <p className="max-w-200 text-[1.125rem] leading-[1.3] tracking-[0.3px]">
                    {description}
                </p>
                <div className="flex min-w-52 items-center gap-8">
                    <Button
                        size="larger"
                        asChild
                        className="rounded-full border-none bg-[rgba(20,20,20,0.18)] fill-white px-8 py-2.5 font-medium text-white backdrop-blur-island md:hover:bg-gray-900 md:hover:fill-white md:hover:text-white"
                    >
                        <a
                            href={ctaUrl}
                            onClick={(e) => {
                                log('DownloadFreeClick');
                                if (!isLoggedIn) {
                                    e.preventDefault();
                                    openLogin(ctaUrl);
                                }
                            }}
                        >
                            {ctaText}
                            <ArrowRight className="ml-2 size-5 transition-all duration-300 hover:fill-white" />
                        </a>
                    </Button>
                    {targetID && (
                        <a
                            className="border-b border-b-white text-white"
                            href={`#${targetID}`}
                        >
                            Watch Now
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
};

interface GuideCardProps {
    /** Data for the specific guide card to display. */
    guideCard: GuideCardData;
    openLogin: (url: string) => void;
}

/**
 * Displays a single guide card, typically shown within the tabbed interface.
 * @param props - Component props.
 * @param props.guideCard - The data for the guide card.
 * @returns The GuideCard component.
 */
const GuideCard = ({ guideCard, openLogin }: GuideCardProps) => {
    const { imageUrl, title, description, ctaText, ctaUrl, fileName } =
        guideCard;
    const { isLoggedIn } = useLoaderData<typeof loader>();
    const tracker = useTracker();

    const log = (eventName: TrackingEvent, guideId?: string) => {
        tracker.log(eventName, {
            data: {
                extraData: {
                    sectionName: 'LandingPagePlan',
                    guideId,
                },
            },
            url: new URL(window.location.href),
        });
    };

    return (
        <div className="grid h-full w-full grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-4">
            {/* Image Section */}
            <div className="flex aspect-[155/139] items-center justify-center md:justify-end">
                <Image
                    className="aspect-[155/139] h-auto w-full object-contain md:h-full md:object-cover"
                    src={imageUrl}
                    alt={title} // Use full title for alt text
                />
            </div>
            {/* Text Content Section */}
            <div className="flex items-center justify-center md:justify-start md:pl-18">
                <div className="flex w-full flex-col text-left md:w-125">
                    <h3 className="text-[1.125rem] font-medium leading-[1.0] tracking-[0.36px]">
                        {title}
                    </h3>
                    <p className="mb-16 mt-6 text-left text-[1.125rem] leading-[1.3] tracking-[0.3px]">
                        {description}
                    </p>
                    <div className="flex items-center justify-start">
                        {/* TODO: Link this button to the actual guide download or relevant page. */}
                        <Button color="primary" size="larger" asChild>
                            <a
                                href={ctaUrl}
                                onClick={(e) => {
                                    log('DownloadFreeClick', fileName);
                                    if (!isLoggedIn) {
                                        e.preventDefault();
                                        openLogin(ctaUrl);
                                    }
                                }}
                            >
                                {ctaText}
                                <ArrowRight className="ml-2 size-5" />
                            </a>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

interface GuideTabContentProps {
    /** Array of guide card data objects for the tab selector. */
    billboardSection: BillboardSection;
    openLogin: (url: string) => void;
}

/**
 * Renders a tabbed interface to switch between different guide cards.
 * @param props - Component props.
 * @param props.guideCards - Data for the guide cards.
 * @returns The GuideTabContent component or null if no cards are provided.
 */
const GuideTabContent = ({
    billboardSection,
    openLogin,
}: GuideTabContentProps) => {
    const { guideCards, title, subtitle } = billboardSection;
    const [selectedGuideId, setSelectedGuideId] = useState<string | undefined>(
        () => guideCards[0]?.id // Initialize directly from prop
    );
    const guideIds = guideCards.map((card) => card.id);
    const selectedGuideRef = useRef<HTMLSpanElement>(null);

    const location = useLocation();
    const guidesRef = useRef<HTMLDivElement>(null);
    const scrolled = useRef(false);

    useEffect(() => {
        if (scrolled.current) {
            return;
        }
        try {
            const urlGuideId = new URLSearchParams(location.search).get(
                'guide'
            );
            if (urlGuideId && guideIds.includes(urlGuideId)) {
                setSelectedGuideId(urlGuideId);
                guidesRef.current?.scrollIntoView({
                    behavior: 'instant',
                    block: 'center',
                });
                scrolled.current = true;
            }
        } catch (error) {
            return;
        }
    }, [guideIds, location.search]);

    useUpdateEffect(() => {
        if (selectedGuideRef.current) {
            selectedGuideRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
            });
        }
    }, [selectedGuideId]);

    // If there are no guide cards, don't render anything
    if (!guideCards || guideCards.length === 0 || !selectedGuideId) {
        return null;
    }

    return (
        <div
            ref={guidesRef}
            className="flex min-h-[70vh] w-full flex-col justify-center bg-white px-4 py-20 sm:px-12 md:min-h-screen md:py-40"
        >
            {/* Added bg-white for contrast */}
            <h2 className="font-regular mb-12 text-center text-9.5 leading-[1.0] md:mb-18 md:text-12">
                {title}
            </h2>
            <div
                className="mb-12 text-center text-base text-gray-600 md:text-lg"
                dangerouslySetInnerHTML={{ __html: subtitle }}
            />
            {/* Toggle Group for Tab Navigation */}
            {guideCards?.length > 1 && (
                <div className="mb-12 flex justify-center md:mb-18">
                    <ToggleGroup
                        type="single"
                        variant="default"
                        value={selectedGuideId} // Now guaranteed to be a string
                        onValueChange={(value) => {
                            if (value) {
                                setSelectedGuideId(value);
                            }
                        }}
                    >
                        {guideCards.map((card) => (
                            <ToggleGroupItem
                                key={card.id}
                                value={card.id}
                                aria-label={`Toggle ${card.title}`} // Use full title for accessibility
                                width="wide"
                            >
                                {/* Extract "Guide X" part for the tab label */}
                                <span
                                    ref={
                                        selectedGuideId === card.id
                                            ? selectedGuideRef
                                            : null
                                    }
                                >
                                    {card.title.split(':')[0]}
                                </span>
                            </ToggleGroupItem>
                        ))}
                    </ToggleGroup>
                </div>
            )}
            {/* Display Area for Selected Guide Card */}
            <div className="relative mx-auto h-166 w-full max-w-7xl md:h-150">
                {/* Adjusted height and max-width */}
                {guideCards.map((card) => (
                    <div
                        key={card.id}
                        // Use conditional rendering for better performance/semantics than visibility/opacity
                        className={cn(
                            'absolute size-full transition-opacity duration-1000 ease-in-out',
                            selectedGuideId === card.id
                                ? 'z-10 opacity-100'
                                : 'pointer-events-none z-0 opacity-0'
                        )}
                        aria-hidden={selectedGuideId !== card.id}
                    >
                        {/* Render the selected GuideCard */}
                        {selectedGuideId === card.id && (
                            <GuideCard guideCard={card} openLogin={openLogin} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

interface ImageBillboardProps {
    /** Data object containing all details for the image billboard. */
    data: ImageBillboardData;
    openLogin: (url: string) => void;
}

/**
 * Displays a billboard section with a prominent image, title, description, CTA, and feature points.
 * @param props - Component props.
 * @param props.data - The data for the image billboard.
 * @returns The ImageBillboard component or null if no data is provided.
 */
const ImageBillboard = ({ data, openLogin }: ImageBillboardProps) => {
    const { isLoggedIn } = useLoaderData<typeof loader>();
    // Early return if data is missing
    if (!data) {
        return null;
    }

    const { title, subtitle, ctaText, ctaUrl, sections, poster, bgTheme } =
        data;

    // Limit sections to a maximum of 3 and truncate long descriptions
    const MAX_SECTIONS = 3;
    const MAX_DESC_LENGTH = 100;
    const limitedSections = (sections || [])
        .slice(0, MAX_SECTIONS)
        .map((section) => ({
            ...section,
            description:
                section.description.length > MAX_DESC_LENGTH
                    ? `${section.description.substring(0, MAX_DESC_LENGTH - 3)}...`
                    : section.description,
        }));

    return (
        <div
            className={cn(
                'flex w-full flex-col items-center px-4 py-25 md:px-12 md:py-40',
                themeColors[bgTheme]
            )}
        >
            {/* Top section: Title, Subtitle, CTA */}
            <div className="mb-10 max-w-4xl md:mb-16 md:text-center">
                <h2 className="font-regular mb-12 text-9.5 leading-[1.0] md:text-14">
                    {title}
                </h2>
                <p className="mb-12 text-base text-gray-600 md:text-lg">
                    {subtitle}
                </p>
                <Button color="primary" size="larger" asChild>
                    <a
                        href={ctaUrl}
                        onClick={(e) => {
                            if (!isLoggedIn) {
                                e.preventDefault();
                                openLogin(ctaUrl);
                            }
                        }}
                    >
                        {ctaText}
                        <ArrowRight className="ml-2 size-5" />
                    </a>
                </Button>
            </div>

            {/* Middle section: Image */}
            <div className="mb-10 w-full max-w-370 md:mb-16">
                <Image
                    src={poster}
                    alt={title} // Use title for alt text
                    className="aspect-video h-auto w-full rounded-lg object-cover shadow-md"
                />
            </div>

            {/* Bottom section: Feature Points/Sections */}
            {limitedSections.length > 0 && (
                <div className="w-full max-w-370">
                    <div className="flex flex-col justify-center gap-3 md:flex-row md:gap-0">
                        {limitedSections.map((section, index) => (
                            <div
                                key={index}
                                className={cn(
                                    'flex-1 border-t border-gray-700 pt-3 md:min-h-36 md:border-l md:border-t-0 md:px-6 md:pb-16 md:pt-0'
                                )}
                            >
                                <p className="text-[1.125rem] font-medium leading-[1.1] text-gray-900">
                                    {section.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

interface VideoBillboardProps {
    /** Data object containing details for the video billboard. */
    data?: VideoBillboardData; // Make data optional to handle potential missing module
    openLogin: (url: string) => void;
}

/**
 * Displays a billboard section featuring a video player.
 * @param props - Component props.
 * @param props.data - The data for the video billboard.
 * @returns The VideoBillboard component or null if no video URL is provided.
 */
const VideoBillboard = ({ data, openLogin }: VideoBillboardProps) => {
    const { isLoggedIn, appDisplayMode } = useLoaderData<typeof loader>();
    const tracker = useTracker();
    // Early return if data or videoUrl is missing
    if (!data) {
        return null;
    }

    const {
        videoUrl,
        posterDesktop,
        posterMobile,
        bgTheme,
        title,
        subtitle,
        ctaText,
        ctaUrl,
        disabled,
        fileName,
    } = data;

    const lastWord = title?.split(' ').pop() || '';
    const sectionName = `LandingPage${lastWord.charAt(0).toUpperCase()}${lastWord.slice(1)}`;
    const log = (eventName: TrackingEvent, guideId?: string) => {
        tracker.log(eventName, {
            data: {
                extraData: {
                    sectionName,
                    guideId,
                },
            },
            url: new URL(window.location.href),
        });
    };
    const poster = appDisplayMode === 'mobile' ? posterMobile : posterDesktop;

    // Define video player rendering logic inside a function for ClientOnly
    const renderVideoPlayer = () => (
        <VideoPlayer
            src={videoUrl}
            onError={(e) => {
                // Log error for debugging, consider more robust error handling/UI feedback
                console.error('Video Player Error:', e);
            }}
            coverSrc={poster}
            playIcon={
                <div className="relative flex size-20 items-center justify-center overflow-hidden rounded-full bg-black/60 transition-all duration-100 hover:scale-105">
                    <div className="absolute inset-0 size-full blur-3xl" />
                    <PlayIcon className="relative z-10 size-10 fill-white" />
                </div>
            }
        />
    );

    return (
        <div
            className={cn(
                'flex w-full flex-col items-center justify-center px-4 py-25 md:px-12 md:py-40',
                themeColors[bgTheme]
            )}
        >
            <div className="mb-10 max-w-4xl md:mb-16 md:text-center">
                <h2 className="font-regular mb-12 text-9.5 leading-[1.0] md:text-14">
                    {title}
                </h2>
                <div
                    className="mb-12 text-base text-gray-600 md:text-lg"
                    dangerouslySetInnerHTML={{ __html: subtitle }}
                />
                {!disabled ? (
                    <Button color="primary" size="larger" asChild>
                        <a
                            href={ctaUrl}
                            onClick={(e) => {
                                if (!isLoggedIn) {
                                    e.preventDefault();
                                    openLogin(ctaUrl);
                                }
                                log('DownloadFreeClick', fileName);
                            }}
                        >
                            {ctaText}
                            <ArrowRight className="ml-2 size-5" />
                        </a>
                    </Button>
                ) : (
                    <div className="mx-auto flex w-66 cursor-pointer items-center justify-center rounded-full bg-[#d9d9d9] px-4 py-2.5 text-base">
                        <span className="text-gray-900 opacity-[0.32]">
                            {ctaText}
                        </span>
                    </div>
                )}
            </div>
            {videoUrl ? (
                <div className="aspect-[385/573] w-full max-w-299 overflow-hidden rounded-lg bg-gray-900 shadow-lg md:aspect-video">
                    <ClientOnly
                        fallback={
                            <Image src={poster} alt="Video placeholder" />
                        }
                    >
                        {renderVideoPlayer}
                    </ClientOnly>
                </div>
            ) : (
                <div className="aspect-[385/573] w-full max-w-299 overflow-hidden rounded-lg shadow-lg md:aspect-video">
                    <Image
                        src={poster}
                        alt="Video placeholder"
                        className="h-full w-full object-cover"
                    />
                </div>
            )}
        </div>
    );
};

interface BillboardsProps {
    /** An array of guide objects, each containing potential billboard data. */
    guides: Guide[];
    openLogin: (url: string) => void;
}

/**
 * Renders a sequence of billboards (Image or Video) based on the provided guides data.
 * @param props - Component props.
 * @param props.guides - The array of guide data.
 * @returns The Billboards container component or null if no guides are provided.
 */
const Billboards = ({ guides, openLogin }: BillboardsProps) => {
    if (!guides || guides.length === 0) {
        return null;
    }

    return (
        <div className="flex w-full flex-col" id="main-content">
            {guides.map((guide) => (
                // Use guide ID for a stable key
                <div key={`billboard-set-${guide.id}`}>
                    {/* Render ImageBillboard if imageModule data exists */}
                    {guide.guideDetail.imageModule && (
                        <ImageBillboard
                            data={guide.guideDetail.imageModule}
                            openLogin={openLogin}
                        />
                    )}
                    {/* Render VideoBillboard if videoModule data exists */}
                    {guide.guideDetail.videoModule && (
                        <VideoBillboard
                            data={guide.guideDetail.videoModule}
                            openLogin={openLogin}
                        />
                    )}
                </div>
            ))}
        </div>
    );
};

interface CarouselItemProps {
    /** Data for a single item within the carousel. */
    item: CarouselItemData;
}

/**
 * Renders a single item within a carousel section.
 * @param props - Component props.
 * @param props.item - The data for the carousel item.
 * @returns The CarouselItem component.
 */
const CarouselItem = ({ item }: CarouselItemProps) => {
    const { imageUrl, title, description } = item;
    return (
        <div className="min-w-0 flex-[0_0_100%] sm:flex-[0_0_50%] md:flex-[0_0_33.33%] lg:flex-[0_0_25%]">
            <div className="overflow-hidden rounded-lg">
                <Image
                    className="block aspect-[592/648] h-auto w-full object-cover"
                    src={imageUrl}
                    alt={title} // Use title for alt text
                    loading="lazy"
                />
            </div>
            <h3 className="mt-4 text-[1rem] font-medium leading-snug md:text-[1.125rem]">
                {description}
            </h3>
        </div>
    );
};

interface CarouselSectionProps {
    /** Data object containing details for the carousel section. */
    carouselData: CarouselData;
    /** The display mode of the carousel. */
    appDisplayMode: 'desktop' | 'mobile';
}

/**
 * Renders a section containing a horizontal carousel of items.
 * @param props - Component props.
 * @param props.carouselData - The data for the carousel section.
 * @returns The CarouselSection component or null if no data is provided.
 */
const CarouselSection = ({
    carouselData,
    appDisplayMode,
}: CarouselSectionProps) => {
    // Early return if data is missing
    if (!carouselData) {
        return null;
    }

    const { title, description, items, bgTheme } = carouselData;

    return (
        <div
            className={cn(
                'w-full py-20 md:py-40 md:pl-12',
                'w-full py-20 md:py-40 md:pl-12',
                themeColors[bgTheme]
            )}
        >
            <HomeCarouselSection
                title={title}
                description={description}
                displayMode={appDisplayMode} // This might need adjustment based on actual responsiveness needs
                size={'small'} // Consider if 'medium' or 'large' might be better
            >
                {items.map((item, index) => (
                    <CarouselItem
                        key={`${item.imageUrl}-${index}`}
                        item={item}
                    />
                ))}
            </HomeCarouselSection>
        </div>
    );
};

/**
 * Renders a split panel section, currently utilizing the HomePageIntro component
 * with predefined content related to joining ArchiPro.
 * This component might evolve to accept dynamic data via props if needed.
 * @returns The SplitPanel component.
 */
const SplitPanel = ({ introData }: { introData: HomePageIntroProps }) => {
    const tracker = useTracker();
    const overriteTracking = () => {
        tracker.log('JoinArchiProClick', {
            url: new URL(window.location.href),
            data: {
                ExtraData: 'LandingPageSlide',
            },
        });
    };
    return (
        <div className="w-full bg-white md:px-12">
            <HomePageIntro
                title={introData.title}
                description={introData.description}
                listItems={introData.listItems}
                CTAButton={introData.CTAButton}
                video={introData.video}
                openLogin={introData.openLogin}
                flexReverse={false}
                overriteTracking={overriteTracking}
            />
        </div>
    );
};

/**
 * The main page component for the "Essential Guides For Homeowners" route.
 * It orchestrates the layout and passes data loaded by the Remix loader
 * down to the relevant sub-components.
 * @returns The EssentialGuidesForHomeowners page component.
 */
const EssentialGuidesForHomeowners = () => {
    // Retrieve all data fetched by the loader
    const {
        bannerVideoModuleData,
        noVideoBanner,
        guides,
        introData,
        campaignPerformanceCarousel,
        testimonialSection,
        // resourcesToGuideYou,
        isMobile,
        appDisplayMode,
        billboardSection,
        isLoggedIn,
    } = useLoaderData<typeof loader>();
    const tracker = useTracker();

    // const fetcher = useFetcher<typeof subscribeEmailAction>();
    const { setLoginModal } = useSetLoginModal();
    const openLogin = (url: string) => {
        if (isLoggedIn || url === '/') {
            // the url is possible to be port 9001 in some cases
            window.location.href = url;
        } else {
            setLoginModal({
                authSource: 'homeModule',
                isOpen: true,
                redirect: url,
                title: url.includes('/app/guides')
                    ? 'Continue to Download'
                    : '',
            });
        }
    };

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory(null);
    }, [setCategory]);

    return (
        <main>
            <BannerVideoModule
                bannerVideoModuleData={bannerVideoModuleData}
                noVideoBanner={noVideoBanner}
                isMobile={isMobile}
                openLogin={openLogin}
            />
            <GuideTabContent
                billboardSection={billboardSection}
                openLogin={openLogin}
            />
            <Billboards guides={guides} openLogin={openLogin} />
            <CarouselSection
                carouselData={campaignPerformanceCarousel}
                appDisplayMode={appDisplayMode}
            />
            <SplitPanel introData={{ ...introData, openLogin }} />
            <div className="bg-[#F2F2F2] md:pl-12">
                <HomePageTestimonials
                    testimonialSection={testimonialSection ?? undefined}
                    appDisplayMode={isMobile ? 'mobile' : 'desktop'}
                    openLogin={openLogin}
                    overriteTracking={() => {
                        tracker.log('StartProjectClick', {
                            url: new URL(window.location.href),
                            data: {
                                ExtraData: 'LandingPageSlide',
                            },
                        });
                    }}
                />
            </div>

            <div className="my-10 flex flex-col gap-10 md:my-[12.8125rem] md:gap-[13.75rem]">
                <PageBottomSection
                    openLogin={openLogin}
                    overriteTracking={(event, sectionName) => {
                        tracker.log(event as TrackingEvent, {
                            url: new URL(window.location.href),
                            data: {
                                ExtraData: sectionName,
                            },
                        });
                    }}
                />
            </div>
        </main>
    );
};

export default EssentialGuidesForHomeowners;
export { loader };
